import { Button, Intent } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import {
  FieldSet,
  IDataTableColumn,
  SortKeysObject,
  useGridState,
  DataTable2,
} from "nsitools-react";
import * as React from "react";
import styled from "styled-components";

import { useTl } from "../../hooks";
import { ETLCodes } from "../../locales";
import { TransfertListDataTable } from "./TransfertListDataTable";

export interface ITransferListProps<T> {
  allItems?: Array<T>;
  keyFieldName: string;
  onChoosenItemsChange: (selectedItems: Array<T>) => void;
  choosenItems?: Array<T>;
  columns: IDataTableColumn[];
  isLoading?: boolean;
  editMode: boolean;
  sortKey?: SortKeysObject;
}

export const CContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;

  & > * + * {
    margin-left: 1rem;
  }
`;
export const BContainer = styled.div`
  & > * + * {
    margin-top: 2rem;
  }
`;
const ButtonsColumns = styled.div`
  display: flex;
  padding-left: 2rem;
  padding-right: 2rem;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  & > * + * {
    margin-top: 0.5rem;
  }
`;
const TransfertListContainer = styled.div`
  flex: 1;
`;

export function TransfertList<T>({
  allItems = [],
  onChoosenItemsChange,
  choosenItems = [],
  columns,
  keyFieldName,
  isLoading = false,
  sortKey,
  editMode,
}: ITransferListProps<T>) {
  const [leftSelectedItems, setLeftSelectedItems] = React.useState<Array<T>>(
    []
  );
  const [rightSelectedItems, setRightSelectedItems] = React.useState<Array<T>>(
    []
  );

  const availableData = React.useMemo(
    () =>
      allItems
        ? allItems.filter(
            (d) =>
              choosenItems.filter((ci) => ci[keyFieldName] === d[keyFieldName])
                .length === 0
          )
        : [],
    [allItems, choosenItems, keyFieldName]
  );

  const { t, tUnsafe } = useTl();

  const addAll = React.useCallback(
    () => onChoosenItemsChange([...choosenItems, ...availableData]),
    [availableData, choosenItems, onChoosenItemsChange]
  );
  const addSelected = React.useCallback(() => {
    onChoosenItemsChange([...choosenItems, ...leftSelectedItems]);
    leftSelectedItems.length = 0;
  }, [choosenItems, leftSelectedItems, onChoosenItemsChange]);

  const removeSelected = React.useCallback(() => {
    onChoosenItemsChange(
      choosenItems.filter(
        (i) =>
          rightSelectedItems.filter(
            (ri) => ri[keyFieldName] === i[keyFieldName]
          ).length === 0
      )
    );
    rightSelectedItems.length = 0;
  }, [choosenItems, keyFieldName, onChoosenItemsChange, rightSelectedItems]);

  const removeAll = React.useCallback(
    () => onChoosenItemsChange([]),
    [onChoosenItemsChange]
  );

  const tableState = useGridState<any>({
    serverMode: false,
    enablePagination: false,
    enableFilter: true,
    availablePageSizes: [5],
    pageSize: 5,
    sortKeys: sortKey ?? {},
  });

  const { setData } = tableState;

  React.useEffect(() => {
    setData(choosenItems);
  }, [choosenItems, setData]);

  return editMode ? (
    <>
      <BContainer>
        <CContainer>
          <TransfertListContainer>
            <FieldSet title={t(ETLCodes.SelectorAvailable)}>
              <TransfertListDataTable
                columns={columns}
                onSelectionChanges={setLeftSelectedItems}
                gridData={availableData}
                loading={isLoading}
                sortKeys={sortKey}
              />
            </FieldSet>
          </TransfertListContainer>
          <ButtonsColumns>
            <Button
              intent={Intent.PRIMARY}
              icon={IconNames.DOUBLE_CHEVRON_RIGHT}
              title={t(ETLCodes.ChooseXItems, {
                count: availableData.length,
              })}
              onClick={addAll}
              disabled={availableData.length === 0}
            />
            <Button
              disabled={leftSelectedItems.length === 0}
              title={t(ETLCodes.ChooseXItems, {
                count: leftSelectedItems.length,
              })}
              intent={Intent.PRIMARY}
              icon={IconNames.CHEVRON_RIGHT}
              onClick={addSelected}
            />
            <Button
              disabled={rightSelectedItems.length === 0}
              title={t(ETLCodes.UnChooseXItems, {
                count: rightSelectedItems.length,
              })}
              intent={Intent.PRIMARY}
              icon={IconNames.CHEVRON_LEFT}
              onClick={removeSelected}
            />
            <Button
              disabled={choosenItems.length === 0}
              intent={Intent.PRIMARY}
              icon={IconNames.DOUBLE_CHEVRON_LEFT}
              onClick={removeAll}
              title={t(ETLCodes.UnChooseXItems, {
                count: choosenItems.length,
              })}
            />
          </ButtonsColumns>
          <TransfertListContainer>
            <FieldSet title={t(ETLCodes.SelectorSelected)}>
              <TransfertListDataTable
                columns={columns}
                onSelectionChanges={setRightSelectedItems}
                gridData={choosenItems}
                loading={isLoading}
                sortKeys={sortKey}
              />
            </FieldSet>
          </TransfertListContainer>
        </CContainer>
      </BContainer>
    </>
  ) : (
    <DataTable2
      tableState={tableState}
      loading={isLoading}
      columns={columns}
      translateFunc={tUnsafe}
    />
  );
}
